import React, { useState } from 'react';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { endOfDay, isValid } from 'date-fns';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useUpdateUserGiftMutation } from '../../redux/features/userGift/userGift.apiSlice';
import { UserGift } from '../../types/userGift';
import { describeGiftError, TARGET_TYPE_LABELS } from './userGiftCopy';

type FormValues = { expiresAt: Date | null; note: string };

const toFormValues = (gift: UserGift | null): FormValues => ({
  expiresAt: gift ? new Date(gift.expiresAt) : null,
  note: gift?.note || '',
});

interface EditUserGiftDialogProps {
  gift: UserGift | null;
  onClose: () => void;
}

/** Expiry and note only. Who the gift is for can't change: disable it and add a new one. */
export function EditUserGiftDialog({ gift, onClose }: EditUserGiftDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [updateGift, { isLoading }] = useUpdateUserGiftMutation();
  const [saveError, setSaveError] = useState('');
  const { values, errors, handleChange, setFieldValue, submitForm } = useFormik<FormValues>({
    initialValues: toFormValues(gift),
    enableReinitialize: true,
    validate: ({ expiresAt }) => {
      // A half-typed date is an Invalid Date: its NaN time passes the "future" check, then toISOString() throws.
      if (!expiresAt || !isValid(expiresAt)) {
        return { expiresAt: 'Enter a valid date' };
      }

      if (endOfDay(expiresAt).getTime() <= Date.now()) {
        return { expiresAt: 'Must be in the future' };
      }

      return {};
    },
    onSubmit: async (formValues) => {
      if (!gift) {
        return;
      }

      const expiresAt = endOfDay(formValues.expiresAt as Date).toISOString();
      const note = formValues.note.trim() || null;

      setSaveError('');

      try {
        await updateGift({
          id: gift.id,
          ...(new Date(gift.expiresAt).toISOString() !== expiresAt && { expiresAt }),
          ...((gift.note || null) !== note && { note }),
        }).unwrap();
        enqueueSnackbar('Gift saved.', { variant: 'success' });
        onClose();
      } catch (err) {
        setSaveError(describeGiftError(err));
      }
    },
  });

  const handleClose = () => {
    setSaveError('');
    onClose();
  };

  return (
    <Dialog open={Boolean(gift)} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit gift #{gift?.id}</DialogTitle>
      <DialogContent>
        {gift && (
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            {TARGET_TYPE_LABELS[gift.targetType]}: <code>{gift.targetValue}</code>
          </Typography>
        )}
        <DatePicker
          label="Expires at"
          value={values.expiresAt}
          onChange={(date) => setFieldValue('expiresAt', date)}
          disablePast
          slotProps={{
            textField: {
              id: 'user-gift-edit-expiresAt',
              name: 'expiresAt',
              fullWidth: true,
              margin: 'normal',
              error: Boolean(errors.expiresAt),
              helperText: errors.expiresAt || 'The gift works until the end of this day',
            },
          }}
        />
        <TextField
          margin="normal"
          fullWidth
          id="user-gift-edit-note"
          name="note"
          label="Note (optional)"
          value={values.note}
          onChange={handleChange}
          inputProps={{ maxLength: 500 }}
        />
        {saveError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {saveError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <LoadingButton
          type="button"
          color="primary"
          variant="contained"
          endIcon={<SaveIcon />}
          loading={isLoading}
          loadingPosition="end"
          onClick={submitForm}
        >
          Save
        </LoadingButton>
        <Button color="inherit" variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
